import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { BASEURL } from '../constant/url';
import { CalendarEvent, CalendarEventAttributes, CalendarEvents, CalendarEventResponse } from '../interfaces/calendar-event';
import { ID_USER } from '../constant/storage';

@Injectable({
    providedIn: 'root'
})
export class CalendarService {
    private readonly _http = inject(HttpClient);
    private readonly API_URL = `${BASEURL}calendar-events`;

    private _events = signal<CalendarEvent[]>([]);

    get events() {
        return this._events;
    }

    private get currentUserId(): number | null {
        const id = localStorage.getItem(ID_USER);
        return id ? parseInt(id, 10) : null;
    }

    fetchEvents(): Observable<CalendarEvent[]> {
        const userId = this.currentUserId;
        if (!userId) return new Observable(sub => sub.next([]));

        return this._http.get<CalendarEvents>(`${this.API_URL}?filters[users_permissions_user][id]=${userId}&pagination[pageSize]=100`).pipe(
            map(response => response.data),
            tap(events => this._events.set(events)),
            catchError(this.handleError)
        );
    }

    addEvent(event: Partial<CalendarEventAttributes>): Observable<CalendarEvent> {
        const userId = this.currentUserId;
        const eventData = {
            ...event,
            users_permissions_user: userId
        };

        return this._http.post<CalendarEventResponse>(this.API_URL, { data: eventData }).pipe(
            map(response => response.data),
            tap(() => this.fetchEvents().subscribe()),
            catchError(this.handleError)
        );
    }

    updateEvent(eventId: number, event: Partial<CalendarEventAttributes>): Observable<CalendarEvent> {
        return this._http.put<CalendarEventResponse>(`${this.API_URL}/${eventId}`, {
            data: event
        }).pipe(
            map(response => response.data),
            tap(() => this.fetchEvents().subscribe()),
            catchError(this.handleError)
        );
    }

    deleteEvent(eventId: number): Observable<void> {
        return this._http.delete<void>(`${this.API_URL}/${eventId}`).pipe(
            tap(() => this.fetchEvents().subscribe()),
            catchError(this.handleError)
        );
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = 'Error en el servicio de Calendario; por favor, inténtelo de nuevo más tarde.';
        if (error.error && error.error.error && error.error.error.message) {
            errorMessage = error.error.error.message;
        } else if (error.message) {
            errorMessage = error.message;
        }
        console.error('CalendarService error:', errorMessage, error.error || error);
        return throwError(() => new Error(errorMessage));
    }
}
